import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, Legend, LinearScale, Tooltip } from 'chart.js';
import PageContainer from '../../../components/PageContainer';
import { EmptyState, PageHeader, StatusChip } from '../../../components/ui';
import { MockupGenerationLogs } from '../../../store/slices';
import { IStore } from '../../../store/types';
import { IMockupGenerationLog, asBool } from '../../../api/mockupGenerationLogs';
import { fmtBool, fmtBytes, fmtDateTime, fmtMs, fmtNum, fmtPct, fmtRatio, fmtSec, fmtSize } from '../../../lib/mockupGenerationLogs/format';
import { LOST_SUBJECT_THRESHOLD } from '../../../lib/mockupGenerationLogs/aggregate';
import { getChartPalette } from '../../../lib/mockupGenerationLogs/palette';
import { DETAIL_TIPS } from '../../../lib/mockupGenerationLogs/tips';
import MockupGenerationLogsInfoTip, { ITip } from '../../../components/MockupGenerationLogsInfoTip';

ChartJS.register(BarElement, CategoryScale, LinearScale, Legend, Tooltip);

function InfoRow({ label, value, warn }: { label: string; value: React.ReactNode; warn?: boolean }) {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="body2" sx={{ minWidth: 190, color: theme.palette.text.secondary, flexShrink: 0 }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', color: warn ? '#d03b3b' : undefined }}>{value}</Typography>
        </Box>
    );
}

function Section({ title, tip, children }: { title: string; tip?: ITip; children: React.ReactNode }) {
    return (
        <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center' }}>
                {title}
                {tip ? <MockupGenerationLogsInfoTip tip={tip} /> : null}
            </Typography>
            {children}
        </Paper>
    );
}

function ScoreBar({ label, value }: { label: string; value: number | null }) {
    const theme = useTheme();
    if (value === null || value === undefined) return <InfoRow label={label} value="—" />;
    const color = value >= 0.8 ? '#0ca30c' : value >= 0.6 ? '#fab219' : '#d03b3b';
    return (
        <Box sx={{ display: 'flex', py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ minWidth: 190, color: theme.palette.text.secondary, flexShrink: 0 }}>{label}</Typography>
            <Box sx={{ width: 120, height: 8, bgcolor: 'action.hover', borderRadius: 4, overflow: 'hidden', mr: 1 }}>
                <Box sx={{ width: `${Math.min(100, Math.max(0, value * 100))}%`, height: '100%', bgcolor: color, borderRadius: 4 }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color }}>{value.toFixed(3)}</Typography>
        </Box>
    );
}

function JsonBlob({ title, value }: { title: string; value: unknown }) {
    const theme = useTheme();
    return (
        <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {title} {value === null || value === undefined ? <Chip size="small" label="null" sx={{ ml: 1 }} /> : null}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        p: 1.5,
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 420,
                        bgcolor: theme.palette.action.hover,
                        borderRadius: 1,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                >
                    {JSON.stringify(value ?? null, null, 2)}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

function ImageCard({ title, url }: { title: string; url: string | null | undefined }) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    return (
        <Paper sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}>{title}</Typography>
            {url ? (
                <>
                    <Box
                        component="button"
                        type="button"
                        aria-expanded={open}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
                        onClick={() => setOpen((v) => !v)}
                        sx={{ p: 0, border: 0, background: 'none', cursor: open ? 'zoom-out' : 'zoom-in', display: 'block', mx: 'auto', maxWidth: '100%' }}
                    >
                        <Box
                            component="img"
                            src={url}
                            alt={title}
                            sx={{ maxWidth: '100%', maxHeight: open ? 'none' : 220, objectFit: 'contain', borderRadius: 1, bgcolor: theme.palette.action.hover, display: 'block' }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        <a href={url} target="_blank" rel="noreferrer">open</a>
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4 }}>—</Typography>
            )}
        </Paper>
    );
}

const OneMockupGenerationLog = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const mode = useSelector<IStore, 'light' | 'dark'>((s) => s.theme?.mode || 'light');
    const pal = useMemo(() => getChartPalette(mode), [mode]);

    const { singleLog: log, singleStatus: status, singleError: error } = useSelector<IStore, IStore['mockupGenerationLogs']>(
        (state) => state.mockupGenerationLogs
    );

    useEffect(() => {
        if (!eventId) return;
        dispatch(MockupGenerationLogs.clearMockupGenerationLog());
        dispatch<any>(MockupGenerationLogs.getMockupGenerationLog(eventId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const stages = useMemo(() => {
        if (!log) return [];
        const s: { label: string; sec: number; color: string }[] = [];
        const push = (label: string, sec: number | null | undefined, color: string) => {
            if (sec !== null && sec !== undefined && sec > 0) s.push({ label, sec, color });
        };
        push('RB inference', (log.rbInferenceMs ?? 0) / 1000, pal.series[0]);
        push('RB FBA', (log.rbFbaMs ?? 0) / 1000, pal.series[1]);
        push('RB postprocess', (log.rbPostprocessMs ?? 0) / 1000, pal.series[2]);
        const rbKnown = ((log.rbInferenceMs ?? 0) + (log.rbFbaMs ?? 0) + (log.rbPostprocessMs ?? 0)) / 1000;
        push('RB other', log.rbSec !== null ? Math.max(0, log.rbSec - rbKnown) : null, pal.series[3]);
        push('3D mesh', log.p3dMeshSec, pal.series[4]);
        push('3D GLB', log.p3dGlbSec, pal.series[5]);
        push('3D previews', log.p3dPreviewsSec, pal.series[6]);
        const known = (log.rbSec ?? 0) + (log.p3dMeshSec ?? 0) + (log.p3dGlbSec ?? 0) + (log.p3dPreviewsSec ?? 0);
        push('Other', log.totalSec !== null ? Math.max(0, log.totalSec - known) : null, pal.series[7]);
        return s;
    }, [log, pal]);

    const back = { to: '/mockup-generation-logs', label: 'Generation logs' };

    if (status === 'loading' || (!log && status !== 'rejected')) {
        return (
            <PageContainer constrained>
                <PageHeader backTo={back} eyebrow="Mockups" title="Generation log" />
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>
            </PageContainer>
        );
    }

    if (!log) {
        return (
            <PageContainer constrained>
                <PageHeader backTo={back} eyebrow="Mockups" title="Generation log" />
                <EmptyState title="Log not found" hint={error || undefined} action={<Button variant="outlined" size="small" onClick={() => navigate('/mockup-generation-logs')}>Back to the list</Button>} />
            </PageContainer>
        );
    }

    const l: IMockupGenerationLog = log;
    const ok = l.status === 'ok';
    const lostSubject = (l.p3dSilIgnoredShare ?? 0) > LOST_SUBJECT_THRESHOLD;
    const overrides = (l.request?.overrides && typeof l.request.overrides === 'object') ? l.request.overrides as Record<string, unknown> : {};

    const stagesData = {
        labels: ['Pipeline'],
        datasets: stages.map((s) => ({ label: `${s.label} (${s.sec.toFixed(3)} s)`, data: [Number(s.sec.toFixed(4))], backgroundColor: s.color, stack: 'p', borderColor: theme.palette.background.paper, borderWidth: 1 })),
    };
    const stagesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y' as const,
        plugins: { legend: { position: 'bottom' as const, labels: { color: pal.text, boxWidth: 12 } }, datalabels: { display: false } },
        scales: {
            x: { stacked: true, ticks: { color: pal.text, callback: (v: any) => `${v} s` }, grid: { color: pal.grid }, beginAtZero: true },
            y: { stacked: true, ticks: { display: false }, grid: { display: false } },
        },
    };

    return (
        <PageContainer constrained>
            <PageHeader
                backTo={back}
                eyebrow="Mockups"
                title={`Event ${l.eventId}`}
                badges={(
                    <>
                        <StatusChip tone={ok ? 'success' : 'danger'}>{`${l.status?.toUpperCase()} ${l.httpStatus ?? ''}`.trim()}</StatusChip>
                        {l.errorKind ? <StatusChip tone="warning">{l.errorKind}</StatusChip> : null}
                        {lostSubject ? <StatusChip tone="warning" dot>{`lost subject: ignoredShare ${fmtRatio(l.p3dSilIgnoredShare, 2)}`}</StatusChip> : null}
                    </>
                )}
                meta={<span>{fmtDateTime(l.ts)}</span>}
            />

            {!ok && (
                <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #d03b3b' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {l.errorJson?.kind || l.errorKind} · HTTP {l.errorJson?.httpStatus || l.httpStatus} {l.errorJson?.stage ? `· stage ${l.errorJson.stage}` : ''}
                    </Typography>
                    <Typography variant="body2">{l.errorJson?.message || l.errorMessage}</Typography>
                </Paper>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center' }}>
                Images
                <MockupGenerationLogsInfoTip tip={DETAIL_TIPS.images} />
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}><ImageCard title="Source image (imageUrl)" url={l.imageUrl} /></Grid>
                <Grid item xs={12} sm={4}><ImageCard title="Mockup result (mockupUrl)" url={l.mockupUrl} /></Grid>
                <Grid item xs={12} sm={4}><ImageCard title="Error image (genImgUrl)" url={l.genImgUrl} /></Grid>
            </Grid>

            {stages.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                        Pipeline timing · total {fmtSec(l.totalSec, 3)}
                        <MockupGenerationLogsInfoTip tip={DETAIL_TIPS.pipeline} />
                    </Typography>
                    <Box sx={{ height: 130 }}><Bar data={stagesData} options={stagesOptions} /></Box>
                </Paper>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Identification" tip={DETAIL_TIPS.identification}>
                        <InfoRow label="eventId" value={l.eventId} />
                        <InfoRow label="tempEventId" value={l.tempEventId ?? '—'} />
                        <InfoRow label="log id" value={l.id} />
                        <InfoRow label="ts (event time, UTC)" value={l.ts} />
                        <InfoRow label="createdAt (mockup event)" value={l.createdAt ?? '—'} />
                        <InfoRow label="serverHost" value={l.serverHost ?? '—'} />
                        <InfoRow label="serverVersion" value={l.serverVersion ?? '—'} />
                        <InfoRow label="customerId" value={l.customerId ?? '—'} />
                        <InfoRow label="sessionId" value={l.sessionId ?? '—'} />
                        <InfoRow label="template" value={l.template ?? '—'} />
                        <InfoRow label="eventStatus (mockup-stats)" value={l.eventStatus ?? '—'} />
                        <InfoRow label="orderExist" value={fmtBool(asBool(l.orderExist))} />
                    </Section>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Result & mode" tip={DETAIL_TIPS.result}>
                        <InfoRow label="status / httpStatus" value={`${l.status} / ${l.httpStatus ?? '—'}`} warn={!ok} />
                        <InfoRow label="errorKind" value={l.errorKind ?? '—'} warn={!!l.errorKind} />
                        <InfoRow label="errorMessage" value={l.errorMessage ?? '—'} warn={!!l.errorMessage} />
                        <InfoRow label="modelType" value={l.modelType ?? '—'} />
                        <InfoRow label="targetResolution" value={l.targetResolution ?? '—'} />
                        <InfoRow label="rbPreview" value={fmtBool(asBool(l.rbPreview))} />
                        <InfoRow label="fastMode / balancedMode" value={`${fmtBool(asBool(l.fastMode))} / ${fmtBool(asBool(l.balancedMode))}`} />
                        <InfoRow label="glbOnly / neutralViewer" value={`${fmtBool(asBool(l.glbOnly))} / ${fmtBool(asBool(l.neutralViewer))}`} />
                        <InfoRow label="noBgProvided (fast path)" value={fmtBool(asBool(l.noBgProvided))} />
                        <InfoRow label="sourceProvided" value={fmtBool(asBool(l.sourceProvided))} />
                        <InfoRow label="includePreviews / previewCount" value={`${fmtBool(asBool(l.includePreviews))} / ${l.previewCount ?? '—'}`} />
                        <InfoRow label="overridesCount / applied" value={`${l.overridesCount ?? '—'} / ${fmtBool(asBool(l.overridesApplied))}`} />
                        {Object.keys(overrides).length > 0 && (
                            <InfoRow label="overrides" value={Object.entries(overrides).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ')} />
                        )}
                        <InfoRow label="filename" value={l.filename ?? '—'} />
                        <InfoRow label="responseMode / bytes" value={`${l.responseMode ?? '—'} / ${fmtBytes(l.responseBytes)}`} />
                    </Section>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Timings" tip={DETAIL_TIPS.timings}>
                        <InfoRow label="totalSec" value={fmtSec(l.totalSec, 3)} />
                        <InfoRow label="rbSec" value={fmtSec(l.rbSec, 3)} />
                        <InfoRow label="rbInferenceMs" value={fmtMs(l.rbInferenceMs)} />
                        <InfoRow label="rbFbaMs" value={fmtMs(l.rbFbaMs)} />
                        <InfoRow label="rbPostprocessMs" value={fmtMs(l.rbPostprocessMs)} />
                        <InfoRow label="p3dMeshSec" value={fmtSec(l.p3dMeshSec, 3)} />
                        <InfoRow label="p3dGlbSec" value={fmtSec(l.p3dGlbSec, 3)} />
                        <InfoRow label="p3dPreviewsSec" value={fmtSec(l.p3dPreviewsSec, 3)} />
                        {l.pillow?.timingsSec && (
                            <InfoRow label="pillow.timingsSec" value={Object.entries(l.pillow.timingsSec).map(([k, v]) => `${k} ${v}s`).join(' · ')} />
                        )}
                        <InfoRow label="mockup processDuration" value={l.processDuration ? `${l.processDuration} s` : '—'} />
                        <InfoRow label="mockup actualMockupDuration" value={l.actualMockupDuration ? `${l.actualMockupDuration} s` : '—'} />
                        <InfoRow label="mockup saveDuration" value={l.saveDuration ? `${l.saveDuration} s` : '—'} />
                    </Section>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Input" tip={DETAIL_TIPS.input}>
                        <InfoRow label="source size" value={fmtSize(l.sourceW, l.sourceH)} />
                        <InfoRow label="sourceMp" value={l.sourceMp ?? '—'} />
                        <InfoRow label="orientationFixed (EXIF)" value={fmtBool(asBool(l.orientationFixed))} />
                        <InfoRow label="blackBordersCropped" value={fmtBool(asBool(l.blackBordersCropped))} />
                        {l.pillow?.trim && (
                            <>
                                <InfoRow label="trim.wasTrimmed" value={fmtBool(l.pillow.trim.wasTrimmed)} />
                                <InfoRow label="trim.bbox" value={l.pillow.trim.bbox?.join(', ') ?? '—'} />
                                <InfoRow label="trim.reason" value={l.pillow.trim.reason ?? '—'} />
                            </>
                        )}
                    </Section>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Background removal" tip={DETAIL_TIPS.backgroundRemoval}>
                        <InfoRow label="rbDevice" value={l.rbDevice ?? '—'} />
                        <InfoRow label="rbSkipped" value={fmtBool(asBool(l.rbSkipped))} />
                        <InfoRow label="rbFbaApplied" value={fmtBool(asBool(l.rbFbaApplied))} />
                        <InfoRow label="rbRetryPerformed / better" value={`${fmtBool(asBool(l.rbRetryPerformed))} / ${l.rbBetterResult ?? '—'}`} />
                        {l.backgroundRemoval?.retryReason && <InfoRow label="retryReason" value={l.backgroundRemoval.retryReason} />}
                        <InfoRow label="rbContentRetention" value={fmtPct(l.rbContentRetention)} />
                        <ScoreBar label="rbOverallQuality" value={l.rbOverallQuality} />
                        <ScoreBar label="rbConfidence" value={l.rbConfidence} />
                        <ScoreBar label="rbEdgeQuality" value={l.rbEdgeQuality} />
                        <InfoRow label="rbAverageAlpha" value={l.rbAverageAlpha ?? '—'} />
                        {l.backgroundRemoval && (
                            <>
                                <InfoRow label="original → final size" value={`${l.backgroundRemoval.originalSizeWh?.join('×') ?? '—'} → ${l.backgroundRemoval.finalSizeWh?.join('×') ?? '—'}`} />
                                <InfoRow label="darkArtifactsRatio" value={fmtRatio(l.backgroundRemoval.darkArtifactsRatio, 4)} />
                                <InfoRow label="semiTransparentRatio" value={fmtRatio(l.backgroundRemoval.semiTransparentRatio, 4)} />
                                <InfoRow label="gpuPostprocessing" value={fmtBool(asBool(l.backgroundRemoval.gpuPostprocessing))} />
                            </>
                        )}
                    </Section>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Section title="Texture & 3D" tip={DETAIL_TIPS.texture3d}>
                        <InfoRow label="texFromSource / texScale" value={`${fmtBool(asBool(l.texFromSource))} / ${l.texScale ?? '—'}`} />
                        {l.textureSource && (
                            <InfoRow label="textureSource" value={`applied ${fmtBool(l.textureSource.applied)} · mask ${l.textureSource.maskSize?.join('×') ?? '—'} · source ${l.textureSource.sourceSize?.join('×') ?? '—'}${l.textureSource.reason ? ` · ${l.textureSource.reason}` : ''}`} />
                        )}
                        <InfoRow label="p3dGlbBytes" value={fmtBytes(l.p3dGlbBytes)} />
                        <InfoRow label="triangles / vertices" value={`${fmtNum(l.p3dTriangles)} / ${fmtNum(l.p3dVertices)}`} />
                        <InfoRow label="outlinePoints" value={l.p3dOutlinePoints ?? '—'} />
                        <InfoRow label="thickness ratio / size" value={`${l.p3dThicknessRatio ?? '—'} / ${l.p3dThicknessSize ?? '—'}${l.pillow?.thicknessBasis ? ` (${l.pillow.thicknessBasis})` : ''}`} />
                        <InfoRow label="silhouette components" value={l.p3dSilComponents ?? '—'} warn={(l.p3dSilComponents ?? 0) > 1} />
                        <InfoRow label="sil ignoredShare" value={fmtRatio(l.p3dSilIgnoredShare, 4)} warn={lostSubject} />
                        <InfoRow label="sil mergedShare" value={fmtRatio(l.p3dSilMergedShare, 4)} />
                        <InfoRow label="p3dTagBuilt" value={fmtBool(asBool(l.p3dTagBuilt))} />
                        {l.pillow?.contourStages && (
                            <InfoRow label="contourStages" value={Object.entries(l.pillow.contourStages).map(([k, v]) => `${k}: ${v}`).join(' · ')} />
                        )}
                        {l.pillow?.stitching && (
                            <InfoRow label="stitching" value={`loops ${l.pillow.stitching.loops} · inner ${l.pillow.stitching.innerLoops} · rim ${l.pillow.stitching.rimPoints} · filled ${l.pillow.stitching.filledTriangles}`} />
                        )}
                        {l.pillow?.heightRange && <InfoRow label="heightRange" value={l.pillow.heightRange.join(' – ')} />}
                        {l.pillow?.textureSize && <InfoRow label="textureSize" value={l.pillow.textureSize.join('×')} />}
                    </Section>
                </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center' }}>
                    Raw blobs
                    <MockupGenerationLogsInfoTip tip={DETAIL_TIPS.rawBlobs} />
                </Typography>
                <JsonBlob title="request" value={l.request} />
                <JsonBlob title="backgroundRemoval" value={l.backgroundRemoval} />
                <JsonBlob title="pillow" value={l.pillow} />
                <JsonBlob title="textureSource" value={l.textureSource} />
                <JsonBlob title="error" value={l.errorJson} />
                <JsonBlob title="full record" value={l} />
            </Paper>
        </PageContainer>
    );
};

export default OneMockupGenerationLog;
